import React from 'react'
import { Column } from 'react-table'
import { SpecialPeriod } from '../../api/special-periods'
import Table from '../table'
import { formatDate } from '../../utils/date'
import { differenceInDays } from 'date-fns'
import { Organization } from '../../api/organizations'
import { Element } from 'react-bulma-components'

interface SpecialPeriodsListProps {
  specialPeriods: SpecialPeriod[]
  onPeriodClick?: (specialPeriod: SpecialPeriod) => void
  showEmployer?: boolean // Whether to show employer column (for superAdmin with multiple employers)
}

/**
 * Displays a table of special periods with their details
 */
const SpecialPeriodsList: React.FC<SpecialPeriodsListProps> = ({
  specialPeriods,
  onPeriodClick,
  showEmployer = false,
}) => {
  const tableColumns = useSpecialPeriodsColumns(specialPeriods, onPeriodClick, showEmployer)

  return (
    <Table<SpecialPeriod>
      columns={tableColumns}
      data={specialPeriods}
      noDataMessage="Aucune période spéciale à afficher"
    />
  )
}

const useSpecialPeriodsColumns = (
  specialPeriods: SpecialPeriod[],
  onClick?: (specialPeriod: SpecialPeriod) => void,
  showEmployer?: boolean,
): Column<SpecialPeriod>[] => {
  return React.useMemo(() => {
    const columns: Column<SpecialPeriod>[] = [
      {
        Header: 'Nom',
        accessor: 'name',
        Cell: (data: any) => {
          const specialPeriod: SpecialPeriod = data.cell.row.original
          return (
            <Element
              renderAs="a"
              color="ghost"
              onClick={() => onClick?.(specialPeriod)}
              data-test={`edit-special-period-${specialPeriod._id}`}
              style={{ cursor: onClick ? 'pointer' : 'default' }}
            >
              {specialPeriod.name}
            </Element>
          )
        },
      },
      {
        Header: 'Type',
        accessor: 'type',
        Cell: (data: any) => {
          const specialPeriod: SpecialPeriod = data.cell.row.original
          return specialPeriod.type
        },
      },
      {
        Header: 'Date de début',
        accessor: 'startDate',
        Cell: (data: any) => {
          const date = typeof data.value === 'string' ? new Date(data.value) : data.value
          return formatDate(date)
        },
      },
      {
        Header: 'Date de fin',
        accessor: 'endDate',
        Cell: (data: any) => {
          const date = typeof data.value === 'string' ? new Date(data.value) : data.value
          return formatDate(date)
        },
      },
      {
        Header: 'Durée',
        id: 'duration',
        Cell: (data: any) => {
          const specialPeriod: SpecialPeriod = data.cell.row.original
          const startDate =
            typeof specialPeriod.startDate === 'string'
              ? new Date(specialPeriod.startDate)
              : specialPeriod.startDate
          const endDate =
            typeof specialPeriod.endDate === 'string'
              ? new Date(specialPeriod.endDate)
              : specialPeriod.endDate
          const days = differenceInDays(endDate, startDate) + 1
          return `${days} jour${days > 1 ? 's' : ''}`
        },
      },
    ]

    // Add employer column if needed
    if (showEmployer) {
      columns.splice(1, 0, {
        Header: 'Employeur',
        accessor: 'employer',
        Cell: (data: any) => {
          const specialPeriod: SpecialPeriod = data.cell.row.original
          const employer =
            typeof specialPeriod.employer === 'string'
              ? null
              : (specialPeriod.employer as Organization)
          return employer ? employer.name : '-'
        },
      })
    }

    return columns
  }, [specialPeriods, onClick, showEmployer])
}

export default SpecialPeriodsList
